import * as React from 'react';

import { formatDate } from '@acceligentllc/shared/utils/time';

import type { ColorPalette } from '@acceligentllc/shared/enums/color';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { BillingCodeEditMode } from 'ab-enums/billingCodeEditMode.enum';

import { Button } from '@acceligentllc/storybook';

import ColorSquare from 'af-components/ColorSquare';
import CustomModal from 'af-components/CustomModal';

import styles from './styles.module.scss';

interface BillingCodeSummary {
	customerId: string;
	unit: Nullable<string>;
	unitPrice: string;
	group: Nullable<string>;
	groupColor: Nullable<ColorPalette>;
	description: string;
	createdAt: Date;
}

interface OwnProps {
	showModal: boolean;
	billingCode: Nullable<BillingCodeSummary>;
	onClose: () => void;
	onSubmit: (mode: BillingCodeEditMode) => void;
}

const BillingCodeEditModeModal: React.FC<OwnProps> = (props) => {
	const {
		showModal,
		billingCode,
		onClose,
		onSubmit,
	} = props;

	const [selectedMode, setSelectedMode] = React.useState<Nullable<BillingCodeEditMode>>(null);

	React.useEffect(() => {
		if (showModal) {
			setSelectedMode(null);
		}
	}, [showModal]);

	const onContinue = React.useCallback(() => {
		if (selectedMode) {
			onSubmit(selectedMode);
		}
	}, [onSubmit, selectedMode]);

	const handleRadioClick = React.useCallback((mode: BillingCodeEditMode) => () => {
		setSelectedMode(mode);
	}, []);

	const renderGroupOption = React.useCallback((option: Nullable<{
		name: string;
		color: Nullable<ColorPalette>;
	}>) => {
		if (!option) {
			return <div />;
		}

		return (
			<div className={styles['group-option']}>
				{option.color && <ColorSquare color={option.color} />}
				{option.name}
			</div>
		);
	}, []);

	const renderBillingCodeDescriptionItem = React.useCallback((item: React.ReactNode, index: number) => (
		<React.Fragment key={index}>
			{index > 0 && <span> • </span>}
			{item}
		</React.Fragment>
	), []);

	const billingCodeDescription = React.useMemo(() => {
		if (!billingCode) {
			return null;
		}

		const items: React.ReactNode[] = [
			billingCode.customerId,
			billingCode.unit,
			billingCode.unitPrice ? `$${billingCode.unitPrice}` : null,
			billingCode.group ? renderGroupOption({
				name: billingCode.group,
				color: billingCode.groupColor,
			}) : null,
			billingCode.description,
			<div key="active-since">
				Active since: {formatDate(billingCode.createdAt, TimeFormat.DATE_ONLY)}
			</div>,
		].filter(Boolean);

		if (!items.length) {
			return null;
		}

		return (
			<div className={styles['billing-code-description']}>
				{items.map(renderBillingCodeDescriptionItem)}
			</div>
		);
	}, [billingCode, renderBillingCodeDescriptionItem, renderGroupOption]);

	return (
		<CustomModal
			closeModal={onClose}
			modalStyle="warning"
			showModal={showModal}
			size="lg"
		>
			<CustomModal.Header
				closeModal={onClose}
				title="How would you like to edit this billing code?"
			/>

			<CustomModal.Body>
				<div className={styles['modal-intro-text']}>
					Each option has different consequences for existing records.
				</div>

				{billingCode && (
					<div className={styles['modal-billing-code-container']}>
						<div className={styles.bold}>Billing Code:</div>
						<div>{billingCodeDescription}</div>
					</div>
				)}

				<div className={styles['radio-label']}>
					<input
						checked={selectedMode === BillingCodeEditMode.DIRECT_EDIT}
						className={styles['radio-label__input']}
						name="billingCodeEditMode"
						onChange={handleRadioClick(BillingCodeEditMode.DIRECT_EDIT)}
						type="radio"
					/>
					<div>
						<strong>Edit & recalculate all history</strong>
						<div>Fixes a wrong price or typo. The corrected value is applied retroactively. Use when a mistake was made.</div>
					</div>
				</div>

				<div className={styles['radio-label']}>
					<input
						checked={selectedMode === BillingCodeEditMode.ARCHIVE_AND_EDIT}
						className={styles['radio-label__input']}
						name="billingCodeEditMode"
						onChange={handleRadioClick(BillingCodeEditMode.ARCHIVE_AND_EDIT)}
						type="radio"
					/>
					<div>
						<strong>Archive and create new</strong>
						<div>Current code is archived. New version takes effect from the date you choose. Best for yearly contract renewals.</div>
					</div>
				</div>
			</CustomModal.Body>

			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={onClose}
					style="secondary"
				/>
				<Button
					disabled={!selectedMode}
					label="Continue"
					onClick={onContinue}
					style="primary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

export default React.memo(BillingCodeEditModeModal);
